import { computed } from '@angular/core';
import { patchState, signalStoreFeature, type, withComputed, withMethods, withState } from '@ngrx/signals';
import { ConstructionProjectLoadModel, ProjectWorkhours } from '@construction/models/construction-project.model';
import { ElementsPlanDatesWbsConfig } from '@core/services/api/construction-geo-api.service';
import moment, { Moment } from 'moment';

/** Returns the applicable work-hours entry for `target` by walking sorted entries and taking the last one whose date is on or before the target. */
function getWorkHoursForDay(project: ConstructionProjectLoadModel, target: Moment): ProjectWorkhours | null {
  const sortedItems = [...project.project_workhours].sort((a, b) => moment(a.date).diff(moment(b.date)));
  let result: ProjectWorkhours | null = null;

  for (let i = 0; i < sortedItems.length; i++) {
    const currentDate = moment(sortedItems[i].date).tz(project.timezone, true);
    const nextDate = i < sortedItems.length - 1 ? moment(sortedItems[i + 1].date).tz(project.timezone, true) : null;

    if (target.isSameOrAfter(currentDate, 'date') && (!nextDate || target.isBefore(nextDate, 'date'))) {
      result = sortedItems[i];
      break;
    }
  }

  return result;
}

/** Reads `project` from upstream state. Owns WBS element weights/visibility state, plan-dates config, and work-hours lookup. */
export function withWbs() {
  return signalStoreFeature(
    { state: type<{ project: ConstructionProjectLoadModel }>() },
    withState({
      elementsWithWeights: [] as string[],
      visibleElements: [] as string[],
      elementsPlanDatesWbsConfig: {} as ElementsPlanDatesWbsConfig,
    }),
    withComputed((store) => ({
      wbsConfig: computed(() => store.project().wbs_config),
    })),
    withMethods((store) => ({
      wbsElementHasWeight(element: string): boolean {
        return store.elementsWithWeights().includes(element);
      },
      isWbsElementVisible(element: string): boolean {
        return store.visibleElements().includes(element);
      },
      updateElementsPlanDatesWbsConfig(elementsPlanDatesWbsConfig: ElementsPlanDatesWbsConfig): void {
        patchState(store, { elementsPlanDatesWbsConfig });
      },
      getProjectWorkHoursForDay(target: Moment): ProjectWorkhours | null {
        return getWorkHoursForDay(store.project(), target);
      },
    })),
  );
}
